//libraries
import React, { useRef, useState, useEffect } from 'react'

// components
import Section from '/react4xp/components/Section'
import SectionDivider from '/react4xp/components/SectionDivider'

import Button from '/react4xp/components/Button'
import Form from '/react4xp/components/Form/Form'
import Input from '/react4xp/components/Form/Input'
import TextArea from '/react4xp/components/Form/TextArea'
import Checkbox from '/react4xp/components/Form/Checkbox'
import Select from '/react4xp/components/Form/Select'
import Upload from '/react4xp/components/Form/Upload'

// svg
import { UxArrowRight } from '/react4xp/components/Svg/Ux/Arrow'

// utils
import { isNonWhitespaceString } from '/lib/reactutils/utils.js'

const RECAPTCHA_SCRIPT_URL = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit'
const RECAPTCHA_TARGET_ID = 'contact-form-recaptcha-target'

declare global {
    interface Window {
        onRecaptchaLoad?: () => void
        grecaptcha?: {
            ready: (cb: () => void) => void
            render: (container: string | HTMLElement, options: { sitekey: string; callback?: () => void }) => number
            getResponse: (widgetId: number) => string
        }
    }
}

// prop types
export interface ContactFormProps {
    dividers?: {
        hideTopDivider?: boolean
        hideBottomDivider?: boolean
    }
    title?: string
    desc?: string
    //form?: 'contact' | 'q-garden',
    user?: {
        firstName?: string
        lastName?: string
        phone?: string
        email?: string
    }
    strings?: {
        popupSuccessClose?: string //Lukk
        popupSuccessTitle?: string //Suksess
        popupSuccessContent?: string //Profilen din ble oppdatert.

        popupErrorClose?: string //Lukk
        popupErrorTitle?: string //Det oppsto en feil under forsøk på å oppdatere innstillingene dine.
        popupErrorContent?: string //Vennligst prøv igjen om noen minutter. Hvis problemet vedvarer, vennligst kontakt oss.

        inputNameLabel?: string //Navn (obligatorisk)
        inputEmailLabel?: string //E-postadresse (obligatorisk)
        inputPhoneLabel?: string //Telefon (obligatorisk)
        inputProductLabel?: string //Gjelder det et produkt?
        inputProductDefaultLabel?: string //Velg produkt
        inputProductNumberLabel?: string //Datostempel og nummer på produkt
        inputTextLabel?: string //Skriv kort hva henvendelsen din gjelder
        inputTextIntro?: string //Ikke skriv personsensitiv informasjon som fødselsnummer og annen personlig informasjon i skjemaet.
        inputTextMaxLimit?: string //tegn
        inputCheckboxLabelBefore?: string //Jeg godkjenner
        inputCheckboxLabelAfter?: string //personvernvilkår

        inputInquiryLabel?: string //Hva gjelder henvendelsen
        inputInquiryDefaultLabel?: string //Velg en
        inputInquiryComplaintLabel?: string //Reklamasjon
        inputInquiryOtherLabel?: string //Annet
        inputUploadLabel?: string//Last opp bilde

        validationRequired?: string //Dette feltet er obligatorisk
        validationRecaptchaRequired?: string //Vennligst fullfør reCAPTCHA-verifiseringen
        validationMaxLength?: string //Maksimalt antall tegn overskredet
        validationMinLength?: string //Meldingen er for kort
        validationMaxUpload?: string //Filstørrelsen overskrider maksimalgrensen på

        submitButton?: string //Send inn
        cancelButton?: string //Avbryt
    }
    sendEmailServiceUrl?: string
    termsAndConditionsUrl?: string
    recaptcha?: { siteKey: string }
    products?: {
        label?: string
        value?: string
    }[]
}

export default function ContactForm({
    dividers = {
        hideTopDivider: false,
        hideBottomDivider: false
    },
    title,
    desc,
    //form,
    user,
    strings,
    sendEmailServiceUrl,
    termsAndConditionsUrl,
    recaptcha,
    products
}: ContactFormProps) {

    const formRef = useRef(null)
    const recaptchaWidgetIdRef = useRef<number | null>(null)
    const [recaptchaReady, setRecaptchaReady] = useState(false)
    const [recaptchaError, setRecaptchaError] = useState(false)

    useEffect(() => {
        if (!recaptcha?.siteKey) return

        const loadScript = () => {
            if (document.querySelector(`script[src^="${RECAPTCHA_SCRIPT_URL.split('?')[0]}"]`)) return
            const script = document.createElement('script')
            script.src = RECAPTCHA_SCRIPT_URL
            script.async = true
            script.defer = true
            document.head.appendChild(script)
        }

        window.onRecaptchaLoad = () => {
            const g = window.grecaptcha
            if (g?.ready) {
                g.ready(() => setRecaptchaReady(true))
            }
        }

        loadScript()
        if (window.grecaptcha?.ready) {
            window.grecaptcha.ready(() => setRecaptchaReady(true))
        }

        return () => {
            delete window.onRecaptchaLoad
        }
    }, [recaptcha?.siteKey])

    useEffect(() => {
        if (!recaptcha?.siteKey || !recaptchaReady) return

        const container = document.getElementById(RECAPTCHA_TARGET_ID)
        if (!container || container.children.length > 0) return

        const g = window.grecaptcha
        if (!g?.render) return

        recaptchaWidgetIdRef.current = g.render(RECAPTCHA_TARGET_ID, {
            sitekey: recaptcha.siteKey,
            callback: () => setRecaptchaError(false),
        })

        return () => {
            recaptchaWidgetIdRef.current = null
        }
    }, [recaptcha?.siteKey, recaptchaReady])

    const getRecaptchaFormData = () => {
        if (!recaptcha?.siteKey || recaptchaWidgetIdRef.current == null) return {}
        const g = window.grecaptcha
        const token = g?.getResponse?.(recaptchaWidgetIdRef.current)
        return token ? { 'g-recaptcha-response': token } : {}
    }

    // make the 2 additional fields below the inquiry hidden by default
    const [showHiddenFields, setShowHiddenFields] = useState(false)

    // if the user selects 'complaint' option, show the 2 additional fields
    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedOption = e.target.options[e.target.selectedIndex]
        const shouldShowFields = selectedOption.id === 'show-additional-fields'
        setShowHiddenFields(shouldShowFields)
    }

    // create an empty state for the selected product
    const [selectedProduct, setSelectedProduct] = useState('')

    // change the above state when the user selects a product
    // we will use this to make the date-stamp and upload fields required / mandatory or not
    const handleProductSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedProduct(e.target.value)
    }

    const inquiries = [
        { name: strings.inputInquiryComplaintLabel, value: 'complaint', makeOtherFieldsRequired: true },
        { name: strings.inputInquiryOtherLabel, value: 'other', makeOtherFieldsRequired: false }
    ]

    return (
        <>
            
            {!dividers?.hideTopDivider && <SectionDivider />}

            <Section className='contact-section'>
                <div className='container'>
                    <div className='row'>
                        <div className='col-md-10 offset-md-1 col-lg-8 offset-lg-2 col-xl-6 offset-xl-3'>

                            {isNonWhitespaceString(title) && (
                                <h2 className='title-top h1-hero-small'>
                                    {title}
                                </h2>  
                            )}

                            {isNonWhitespaceString(desc) && (
                                <p className='desc'>
                                    {desc}
                                </p>  
                            )}

                            <div className='contact-form'>

                                <Form
                                    isFormData
                                    ref={formRef}
                                    endpoint={sendEmailServiceUrl}
                                    getExtraFormData={recaptcha?.siteKey ? getRecaptchaFormData : undefined}
                                    validateBeforeSubmit={recaptcha?.siteKey ? () => !!getRecaptchaFormData()['g-recaptcha-response'] : undefined}
                                    onValidateBeforeSubmitFail={recaptcha?.siteKey ? () => setRecaptchaError(true) : undefined}
                                    popupSuccessContent={{
                                        hasCloseButton: true,
                                        closeButtonText: strings.popupSuccessClose,
                                        title: strings.popupSuccessTitle,
                                        content: strings.popupSuccessContent,
                                        actionButtons: {
                                            primary: {
                                                text: strings.popupSuccessClose,
                                                closeOnClick: true
                                            }
                                        },
                                        onClose: () => {
                                            window.location.reload()
                                        }
                                    }}
                                    popupErrorContent={{
                                        hasCloseButton: true,
                                        closeButtonText: strings.popupErrorClose,
                                        title: strings.popupErrorTitle,
                                        content: strings.popupErrorContent,
                                        actionButtons: {
                                            primary: {
                                                text: strings.popupErrorClose,
                                                closeOnClick: true
                                            }
                                        }
                                    }}
                                >

                                    <Input
                                        name='name'
                                        id='contact-form-name'
                                        label={strings.inputNameLabel}
                                        type='text'
                                        size='full-width'
                                        required
                                        value={`${user?.firstName || ''} ${user?.lastName || ''}`.trim()}
                                        errorMessages={{
                                            required: strings.validationRequired
                                        }}
                                    />

                                    <Input
                                        name='email'
                                        id='contact-form-email'
                                        label={strings.inputEmailLabel}
                                        type='email'
                                        size='large'
                                        required
                                        value={user?.email || ''}
                                        errorMessages={{
                                            required: strings.validationRequired
                                        }}
                                    />

                                    <Input
                                        name='phone'
                                        id='contact-form-phone'
                                        label={strings.inputPhoneLabel}
                                        type='tel'
                                        size='medium'
                                        required
                                        value={user?.phone || ''}
                                        errorMessages={{
                                            required: strings.validationRequired
                                        }}
                                    />

                                    <br />

                                    <Select
                                        name='inquiry'
                                        id='contact-inquiry'
                                        label={strings.inputInquiryLabel}
                                        size='large'
                                        required
                                        defaultValue=''
                                        onChange={handleSelectChange}
                                        errorMessages={{
                                            required: strings.validationRequired
                                        }}
                                    >
                                        
                                        <option value='' disabled>
                                            {strings.inputInquiryDefaultLabel}
                                        </option>

                                        {inquiries.map((inquiry, index) => (
                                            <option
                                                key={index}
                                                value={inquiry.value}
                                                id={inquiry.makeOtherFieldsRequired ? 'show-additional-fields' : null}
                                            >
                                                {inquiry.name}
                                            </option>
                                        ))}

                                    </Select>

                                    {showHiddenFields && (
                                        <>
                                            <Select
                                                defaultValue=''
                                                label={strings.inputProductLabel}
                                                id='contact-form-products'
                                                size='large'
                                                name='product'
                                                required
                                                onChange={handleProductSelectChange}
                                                errorMessages={{
                                                    required: strings.validationRequired
                                                }}
                                            >
                                                <option value=''>
                                                    {strings.inputProductDefaultLabel}
                                                </option>

                                                {products && products.map((product, i) => (
                                                    <option key={i} value={product.value}>
                                                        {product.label}
                                                    </option>
                                                ))}

                                            </Select>

                                            <Input
                                                name='product_number'
                                                id='contact-product-number'
                                                label={strings.inputProductNumberLabel}
                                                type='text'
                                                required
                                                errorMessages={{
                                                    required: strings.validationRequired
                                                }}
                                                size='full-width'
                                            />

                                        </>
                                    )}

                                    <p className='text-small textarea'>
                                        {strings.inputTextLabel}
                                    </p>

                                    <p className='text-smallest'>
                                        {strings.inputTextIntro}
                                    </p>

                                    <TextArea
                                        id='contact-form-comment'
                                        name='text'
                                        required
                                        size='full-width'
                                        minLength={5}
                                        maxLength={{
                                            limit: 2000,
                                            text: strings.inputTextMaxLimit
                                        }}
                                        errorMessages={{
                                            required: strings.validationRequired,
                                            maxLength: strings.validationMaxLength,
                                            minLength: strings.validationMinLength
                                        }}
                                    />

                                    {/*
                                    <Upload
                                        id='contact-upload'
                                        label={strings.inputUploadLabel}
                                        name='upload-file'
                                        className='upload'
                                        accept='image/*, application/pdf, application/zip'
                                        multiple={true}
                                        maxFileSize={25} // this will always be mb
                                        errorMessages={{
                                            required: strings.validationRequired,
                                            maxFileSize: strings.validationMaxUpload // this message will only appear in an alert if the use uploads a file larger than 25mb
                                        }}
                                    />
                                    */}

                                    <Checkbox
                                        id='contact-form-terms'
                                        name='terms'
                                        className='checkbox'
                                        required
                                        errorMessages={{
                                            required: strings.validationRequired
                                        }}
                                    >
                                        {strings.inputCheckboxLabelBefore} <a href={termsAndConditionsUrl}>{strings.inputCheckboxLabelAfter}</a>
                                    </Checkbox>

                                    {recaptcha?.siteKey && (
                                        <>
                                            <style>{`.grecaptcha-badge, #${RECAPTCHA_TARGET_ID} { z-index: 101 !important; }`}</style>
                                            <div
                                                id={RECAPTCHA_TARGET_ID}
                                                aria-label="reCAPTCHA verification"
                                                style={{ minHeight: 78 }}
                                            />
                                            {recaptchaError && (
                                                <p className="text-small text-error" role="alert">
                                                    {strings.validationRecaptchaRequired || strings.validationRequired}
                                                </p>
                                            )}
                                        </>
                                    )}

                                    <div className='two-buttons'>

                                        <Button
                                            type='submit'
                                            noUnderline
                                        >
                                            {strings.submitButton}
                                        </Button>

                                        <Button
                                            type='button'
                                            noUnderline
                                            noBorders
                                            className='cancel-button'
                                            onClick={() => {
                                                formRef.current?.reset()
                                                document.dispatchEvent(new Event('resetForm'))
                                            }}
                                        >
                                            {strings.cancelButton} <UxArrowRight />
                                        </Button>

                                    </div>
                                </Form>

                                {/*
                                {form === 'q-garden' && (
                                    <Form
                                        isFormData
                                        ref={formRef}
                                        endpoint={sendEmailServiceUrl}
                                        clearOnSuccess={true}
                                        popupSuccessContent={{
                                            hasCloseButton: true,
                                            closeButtonText: strings.popupSuccessClose,
                                            title: strings.popupSuccessTitle,
                                            content: strings.popupSuccessContent,
                                            actionButtons: {
                                                primary: {
                                                    text: strings.popupSuccessClose,
                                                    closeOnClick: true
                                                }
                                            }
                                        }}
                                        popupErrorContent={{
                                            hasCloseButton: true,
                                            closeButtonText: strings.popupErrorClose,
                                            title: strings.popupErrorTitle,
                                            content: strings.popupErrorContent,
                                            actionButtons: {
                                                primary: {
                                                    text: strings.popupErrorClose,
                                                    closeOnClick: true
                                                }
                                            }
                                        }}
                                    >

                                        <Input
                                            name='name'
                                            id='contact-form-name'
                                            label={strings.inputNameLabel}
                                            type='text'
                                            size='full-width'
                                            required
                                            value={`${user?.firstName || ''} ${user?.lastName || ''}`.trim()}
                                            errorMessages={{
                                                required: strings.validationRequired
                                            }}
                                        />

                                        <Input
                                            name='email'
                                            id='contact-form-email'
                                            label={strings.inputEmailLabel}
                                            type='email'
                                            size='large'
                                            required
                                            value={user?.email || ''}
                                            errorMessages={{
                                                required: strings.validationRequired
                                            }}
                                        />

                                        <Input
                                            name='phone'
                                            id='contact-form-phone'
                                            label={strings.inputPhoneLabel}
                                            type='tel'
                                            className='margin-bottom'
                                            size='medium'
                                            required
                                            value={user?.phone || ''}
                                            errorMessages={{
                                                required: strings.validationRequired
                                            }}
                                        />

                                        <p className='text-small'>
                                            {strings.inputTextLabel}
                                        </p>

                                        <p className='text-smallest'>
                                            {strings.inputTextIntro}
                                        </p>

                                        <TextArea
                                            id='contact-form-comment'
                                            name='text'
                                            required
                                            size='full-width'
                                            minLength={5}
                                            maxLength={{
                                                limit: 2000,
                                                text: strings.inputTextMaxLimit
                                            }}
                                            errorMessages={{
                                                required: strings.validationRequired,
                                                maxLength: strings.validationMaxLength,
                                                minLength: strings.validationMinLength
                                            }}
                                        />

                                        <Checkbox
                                            id='contact-form-terms'
                                            name='terms'
                                            className='margin-bottom'
                                            required
                                            errorMessages={{
                                                required: strings.validationRequired
                                            }}
                                        >
                                            {strings.inputCheckboxLabelBefore} <a href={termsAndConditionsUrl}>{strings.inputCheckboxLabelAfter}</a>
                                        </Checkbox>

                                        <Input id='mode' type='hidden' name='mode' value='q-garden' />

                                        <div className='two-buttons'>

                                            <Button
                                                isSolid
                                                type='submit'
                                                noUnderline
                                            >
                                                {strings.submitButton}
                                            </Button>

                                            <Link
                                                type='button'
                                                text={strings.cancelButton}
                                                onClick={() => {
                                                    formRef.current?.reset(),
                                                    document.dispatchEvent(new Event('resetForm'))
                                                }}
                                            />

                                        </div>
                                    </Form>
                                )}
                                */}

                            </div>

                        </div>
                    </div>
                </div>
            </Section>

            {!dividers?.hideBottomDivider && <SectionDivider />}

        </>
    )
}